import { put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { ApiResult } from 'common/models';
import { EntityType, MessageType } from 'common/enums';
import {
    DELETE_POPUP_MSG,
    DELETE_POPUP_SUCCESS,
    FAILURE_HEADER,
    RESTORE_SUCCESS,
    SUCCESS_HEADER
} from 'common/config';
import { appStateActions } from 'common/redux/app-state';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { Entity, EntityVM } from 'common/models/data/entity.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { findCategories } from 'common/utils/table';
import { hasValue } from 'common/utils/data';

import { ArchiveService } from './archive.service';
import { archiveActions } from './archive.slice';
import { getArchiveList, getHierarchicalDataSelector } from './archive.selector';
import { ArchiveListRequest } from '../models/archive.model';
import { MasterDataService } from 'modules/master-data/redux/masterData.service';

const fetchArchiveList = function* ({ payload }: PayloadAction<ArchiveListRequest>) {
    const { hasErrors, errors, value } = yield ArchiveService.fetchArchiveList(
        payload.pagedRequest
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: archiveActions.fetchArchiveListError.type,
            payload: error
        });
    } else {
        yield put({
            type: archiveActions.fetchArchiveListSuccess.type,
            payload: value
        });
    }
};

const fetchArchiveListWatch = function* () {
    yield takeLatest(archiveActions.fetchArchiveList.type, fetchArchiveList);
};

const getEntitiesByCategory = function* ({ payload }: PayloadAction<EntityVM>) {
    const archiveData: Entity[] = yield select(getArchiveList);
    const subCats = findCategories(archiveData, payload.id);
    if (!payload.isExpanded && !hasValue(subCats.subCategories)) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        const categories: ApiResult<Entity[] | IErrorApiResponse> | undefined =
            yield ArchiveService.getEntitiesById(payload.id, payload.entityType);
        if (categories && categories.hasErrors && categories.errors) {
            yield put({
                type: archiveActions.toggleExpansionFailed.type,
                payload: categories.errors
            });
            yield put({
                type: appStateActions.showLoader.type,
                payload: false
            });
        } else {
            yield put({
                type: archiveActions.toggleExpansionSuccess.type,
                payload: {
                    parent: payload,
                    subCategories: categories?.value
                }
            });
            yield put({
                type: appStateActions.showLoader.type,
                payload: false
            });
        }
    } else {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

const getEntitiesByCategoryWatch = function* () {
    yield takeLatest(archiveActions.toggleExpansion.type, getEntitiesByCategory);
};

const getSearchList = function* ({ payload }: PayloadAction<any>) {
    const searchList = ArchiveService.getSearchList(payload);
    yield put({
        type: archiveActions.searchListSuccess,
        payload: searchList
    });
};

const getSearchListWatch = function* () {
    yield takeLatest(archiveActions.searchList.type, getSearchList);
};

const deleteArchivedData = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors }: ApiResult<any> = yield ArchiveService.deleteArchiveData(
        payload.entityId,
        payload.entityType
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: archiveActions.deleteArchiveDataError.type,
            payload: error
        });
    } else {
        yield put({
            type: archiveActions.deleteArchiveDataSuccess.type,
            payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: DELETE_POPUP_SUCCESS,
                message: DELETE_POPUP_MSG,
                type: MessageType.Success
            }
        });
    }
};

const deleteArchivedDataWatch = function* () {
    yield takeLatest(archiveActions.deleteArchiveDataBegin.type, deleteArchivedData);
};

const getHierarchicalData = function* ({ payload }: PayloadAction<any>) {
    console.log(payload);
    const categories: ApiResult<PaginatedApiResponse<Entity[]> | IErrorApiResponse> | undefined =
        yield ArchiveService.getCategories(payload.pagedRequest, payload.entityId, payload.type);
    if (categories && categories.hasErrors && categories.errors) {
        yield put({
            type: archiveActions.getHierarchicalDataError.type,
            payload: categories.errors
        });
    } else {
        yield put({
            type: archiveActions.getHierarchicalDataSuccess.type,
            payload: categories?.value
        });
    }
};

const getHierarchicalDataWatch = function* () {
    yield takeLatest(archiveActions.getHierarchicalData.type, getHierarchicalData);
};
const getHierarchicalDataByCategory = function* ({ payload }: PayloadAction<EntityVM>) {
    const masterData: Entity[] = yield select(getHierarchicalDataSelector);
    const subCats = findCategories(masterData, payload.id);
    if (!payload.isExpanded && !hasValue(subCats.subCategories)) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        let categories: ApiResult<IErrorApiResponse | Entity[]> = {
            errors: [],
            hasErrors: false
        };
        if (payload.entityType === EntityType.Category)
            categories = yield MasterDataService.getEntitiesByCategoryId(payload.id);
        else categories = yield MasterDataService.getEntitiesByHierarchyId(payload.hierarchyId);
        if (categories && categories.hasErrors && categories.errors) {
            yield put({
                type: archiveActions.toggleHierarchicalDataExpansionFailed.type,
                payload: categories.errors
            });
        } else {
            yield put({
                type: archiveActions.toggleHierarchicalDataExpansionSuccess.type,
                payload: {
                    parent: payload,
                    subCategories: categories?.value
                }
            });
        }
    } else {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

const getHierarchicalDataByCategoryWatch = function* () {
    yield takeLatest(
        archiveActions.toggleHierarchicalDataExpansion.type,
        getHierarchicalDataByCategory
    );
};

const restore = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield ArchiveService.restore(payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: error,
                type: MessageType.Error
            }
        });
        yield put({
            type: archiveActions.restoreFailed.type,
            payload: error
        });
    } else {
        yield put({
            type: archiveActions.restoreSuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: RESTORE_SUCCESS,
                type: MessageType.Success
            }
        });
        if (payload.entityType !== EntityType.Category) {
            yield put({
                type: archiveActions.refreshEntitiesByCategory.type,
                payload: {
                    entityType: payload.parentHierarchyId
                        ? EntityType.SubCategory
                        : EntityType.Category,
                    entityId: payload.parentHierarchyId
                        ? payload.parentHierarchyId
                        : payload.categoryId
                }
            });
        }
    }
};

const restoreWatch = function* () {
    yield takeLatest(archiveActions.restore.type, restore);
};

const fetchArchiveListBySearch = function* ({ payload }: PayloadAction<ArchiveListRequest>) {
    const { hasErrors, errors, value } = yield ArchiveService.fetchArchiveListBySearch(payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: archiveActions.fetchArchiveListBySearchError.type,
            payload: error
        });
    } else {
        yield put({
            type: archiveActions.fetchArchiveListBySearchSuccess.type,
            payload: value
        });
    }
};

const fetchArchiveListBySearchWatch = function* () {
    yield takeLatest(archiveActions.fetchArchiveListBySearchBegin.type, fetchArchiveListBySearch);
};

export {
    fetchArchiveListWatch,
    getSearchListWatch,
    deleteArchivedDataWatch,
    getHierarchicalDataWatch,
    getHierarchicalDataByCategoryWatch,
    restoreWatch,
    getEntitiesByCategoryWatch,
    fetchArchiveListBySearchWatch
};
